import { type JSX } from 'react';

import { BeGambleAware, Picture } from '@cx-modules/ui';

import { useOddsGridContext } from '../../context/OddsGridContext';
import { useBookmakerData } from '../../hooks/useBookmakerData';
import { useSelector } from '../../providers/StoreProvider';
import { type Bookmaker, type Offer } from '../../types';
import { BookmakerClickOut } from '../BookmakerClickOut';

import { bestContrast } from './colour-contrast-helper';
import {
  offerBookmakerImg,
  OfferItemCta,
  OfferItemDescription,
  OfferItemHeading,
  OfferItemLabel,
  OfferItemTop,
  OfferItemWrapper,
  OfferResponsibleGamingContainer,
} from './style';

export const OffersListItem = ({
  bookmaker,
  eventLabel,
  index,
  offer,
}: {
  bookmaker: Bookmaker;
  eventLabel: string;
  index: number;
  offer: Offer;
}): JSX.Element => {
  const repub = useSelector(state => state.repub);
  const autoContrastOfferTooltips = useSelector(state => state.autoContrastOfferTooltips);
  const { oddsShown } = useOddsGridContext();
  const { bookmakerData } = useBookmakerData(bookmaker.code);

  const isNonClickable = oddsShown === 'OC_NON_CLICKABLE';

  const { code, name } = bookmaker;
  const { brandColour, logoLarge, textColour: textColourFromBookmaker = 'light' } = bookmakerData;

  const textColour = autoContrastOfferTooltips ? bestContrast(brandColour, ['dark', 'light']) : textColourFromBookmaker;

  const { customerType = '' } = offer;
  const label = customerType.toLowerCase() === 'new' ? 'New Customer Offer' : 'All Customer Offer';

  return (
    <OfferItemWrapper backgroundColor={brandColour} className="offer-item-wrapper" key={`${offer.id}_${index}`}>
      <OfferItemTop>
        <Picture altText={name} className={offerBookmakerImg} height={15} svgSrc={logoLarge} width={57} />
        <OfferItemLabel textColour={textColour}>{label}</OfferItemLabel>
      </OfferItemTop>
      <OfferItemHeading data-testid="offer-title" textColour={textColour}>
        {offer.title}
      </OfferItemHeading>
      <OfferItemDescription data-testid="offer-desc" textColour={textColour}>
        {offer.content}
      </OfferItemDescription>
      <OfferResponsibleGamingContainer>
        <a
          href="https://www.begambleaware.org/"
          rel="noopener noreferrer nofollow sponsored"
          target="_blank"
          title="BeGambleAware"
        >
          <BeGambleAware />
        </a>
      </OfferResponsibleGamingContainer>
      <BookmakerClickOut
        bookmakerCode={code}
        eventLabel={eventLabel}
        href={offer.clickOutUrl}
        isNonClickable={isNonClickable}
        repub={repub}
        title={name}
      >
        <OfferItemCta data-testid="offer-cta">Get Offer</OfferItemCta>
      </BookmakerClickOut>
    </OfferItemWrapper>
  );
};
